import { Box, Button, Icon, Stack, Typography } from '@ace-clublabs/shared-component-library';
import { useEffect, useState } from 'react';
import { NewJoinSessionData } from '../../application/models/NewJoinSessionData';
import maskCardImage from '../../assets/mask-card-image.svg';
import {
  formatFullName,
  getDate7DaysLaterFormatted,
  getMembershipTitle,
  startDateFromNowInDays,
} from '../../utils/helpers';
import { MembershipLevel } from '@ace-clublabs/common-membership-api-types';

interface IGiftFlowOverviewSection {
  sessionData?: NewJoinSessionData;
  numOfAssociates?: number;
  rvAdded?: boolean;
}

export const GiftFlowOverviewSection = ({ sessionData, numOfAssociates, rvAdded }: IGiftFlowOverviewSection) => {
  const confirmationInfo = sessionData!.confirmationData;
  const memNumber = confirmationInfo?.membershipNumber;
  const iconMap: Record<string, string> = {
    VISA: 'visa-logo',
    MASTERCARD: 'mastercard-logo',
    DISCOVER: 'discover-logo',
    //no working amex card to get valid amex brand name, covering both scenarios
    'AMERICAN EXPRESS': 'amex-logo',
    AMERICANEXPRESS: 'amex-logo',
  };

  const [boxHeight, setBoxHeight] = useState('auto');

  const updateBoxHeight = () => {
    const overviewSection = document.querySelector('.gift-membership-overview') as HTMLElement;
    if (overviewSection) {
      setBoxHeight(`${overviewSection.offsetHeight + 48}px`); // Set the blue box background height to the size of the container
    }
  };

  useEffect(() => {
    updateBoxHeight();
    window.addEventListener('resize', updateBoxHeight);
    return () => {
      window.removeEventListener('resize', updateBoxHeight);
    };
  }, []);

  const startDate = sessionData?.formData.giftDetails?.startDate;

  const effectiveDateDiffInDays = Math.floor(startDateFromNowInDays(startDate!));

  let displayEffDate = false;
  const greaterEqualThan7Days = effectiveDateDiffInDays >= 7;

  if (
    !greaterEqualThan7Days &&
    confirmationInfo?.membershipLevel !== MembershipLevel.CLASSIC &&
    confirmationInfo?.membershipLevel !== MembershipLevel.CLASSIC_RV
  ) {
    displayEffDate = true;
  }

  const getIconName = (cardBrand: string): string => {
    return iconMap[cardBrand] || '';
  };
  const paymentIconName = getIconName(confirmationInfo?.paymentDetails?.card?.brand!);

  const membershipTitle = getMembershipTitle(confirmationInfo?.membershipLevel as MembershipLevel, rvAdded);

  const paymentType =
    confirmationInfo?.paymentDetails?.paymentType === 'CCRD' ||
    confirmationInfo?.paymentDetails?.paymentType === 'Credit Card'
      ? 'Credit card'
      : 'Debit card';

  return (
    <Stack marginTop={'1.5rem'} className="gift-overview-container">
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          backgroundColor: '#EFF7FE',
          zIndex: -1,
          width: '100vw',
          height: boxHeight,
        }}
      />
      <div className="gift-membership-overview">
        <div className="membership-overview-container">
          <Box className="membership-overview-header">
            <Typography variant="h6Medium" className="gift-flow-overview-header">
              Gift membership details
            </Typography>
          </Box>

          <Stack className="membership-overview-info-container" gap={'1rem'}>
            <hr className="membership-overview-line"></hr>
            <Stack className="membership-overview-item" direction="column">
              <Typography className="start-date-label" variant="b2Regular" sx={{ color: '#707070' }}>
                Membership start date:
              </Typography>
              <Typography variant="b1Medium" data-di-mask="true" className="qm-mask selected-start-date">
                {startDate}
              </Typography>
            </Stack>

            <Stack className="membership-overview-item" direction="column">
              <Typography className="benefit-level-label" variant="b2Regular" sx={{ color: '#707070' }}>
                Benefit level:
              </Typography>
              <Typography className="benefit-level-name" variant="b1Medium">
                {membershipTitle} member
              </Typography>

              {displayEffDate && (
                <Typography className="membership-effective-date" variant="b2Regular" sx={{ color: '#681DA8' }}>
                  Effective on {getDate7DaysLaterFormatted()}
                </Typography>
              )}
            </Stack>

            <Stack className="membership-overview-item" direction="column">
              <Typography className="membership-number-label" variant="b2Regular" sx={{ color: '#707070' }}>
                Membership #:
              </Typography>
              <Typography variant="b1Medium" data-di-mask="true" className="qm-mask membership-number-detail">
                {memNumber!.slice(0, 3)} {memNumber!.slice(3, 6)} {memNumber!.slice(6)}
              </Typography>
            </Stack>

            <Stack className="membership-overview-item" direction="column">
              <Typography className="primary-member-label" variant="b2Regular" sx={{ color: '#707070' }}>
                Primary member:
              </Typography>
              <Typography variant="b1Medium" data-di-mask="true" className="qm-mask primary-member-name">
                {formatFullName(confirmationInfo?.firstName, confirmationInfo?.lastName)}
              </Typography>
            </Stack>

            {numOfAssociates !== undefined && numOfAssociates > 0 && (
              <Stack className="membership-overview-item" direction="column">
                <Typography className="family-member-label" variant="b2Regular" sx={{ color: '#707070' }}>
                  Family members:
                </Typography>
                <Typography className="family-member-details">
                  {confirmationInfo?.members.map((member, index) => {
                    const { firstName, lastName } = member;
                    const fullName =
                      index < confirmationInfo.members.length - 1
                        ? `${formatFullName(firstName, lastName)}, `
                        : `${formatFullName(firstName, lastName)}`;
                    return (
                      <Typography
                        variant="b1Medium"
                        key={fullName}
                        data-di-mask="true"
                        className={`member-${firstName}-${lastName} membership-overview-content qm-mask`}
                      >
                        {fullName}
                      </Typography>
                    );
                  })}
                </Typography>
              </Stack>
            )}

            {rvAdded && (
              <Stack className="membership-overview-item" direction="column">
                <Typography className="add-on-label" variant="b2Regular" sx={{ color: '#707070' }}>
                  Add-ons:
                </Typography>
                <Typography variant="b1Medium" key={'rv'} data-di-mask="true" className={`option-rv qm-mask`}>
                  Roadside service for RV/motorcycle
                </Typography>
                {!greaterEqualThan7Days && (
                  <Typography className="rv-effective-date" variant="b2Regular" sx={{ color: '#681DA8' }}>
                    Effective on {getDate7DaysLaterFormatted()}
                  </Typography>
                )}
              </Stack>
            )}
          </Stack>
        </div>
        <div className="membership-overview-container">
          <Box className="membership-overview-header">
            <Typography variant="h6Medium" className="payment-detail-header">
              Payment details
            </Typography>
          </Box>

          <Stack className="membership-overview-info-container" gap={'1rem'}>
            <hr className="membership-overview-line"></hr>
            <Stack className="membership-overview-item" direction="column">
              <Typography className="amount-paid-title" variant="b2Regular" sx={{ color: '#707070' }}>
                Amount paid today:
              </Typography>
              <Typography variant="b1Medium" data-di-mask="true" className="qm-mask amount-paid-detail">
                ${confirmationInfo?.amount}
              </Typography>
            </Stack>

            <Stack className="membership-overview-item" direction="column">
              <Typography className="payment-method-title" variant="b2Regular" sx={{ color: '#707070' }}>
                Payment method:
              </Typography>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography className="payment-type" variant="b1Medium">
                  {paymentType}
                </Typography>
                <Stack className="payment-info-container" direction="row" alignItems="center" gap={'0.375rem'}>
                  {paymentIconName && (
                    <Icon
                      className={`payment-brand-icon-${paymentIconName}`}
                      sx={{ fontSize: '2.25rem' }}
                      iconName={paymentIconName as any}
                    ></Icon>
                  )}
                  <img src={maskCardImage} alt="mask-card-image" className="mask-card-image" />
                  <Typography className="card-last-four" variant="b1Medium">
                    {confirmationInfo?.paymentDetails?.card?.last4}
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        </div>
      </div>

      <Button
        label="Go back to home"
        fullWidth
        onClick={() => {
          window.open('https://www.ace.aaa.com/');
        }}
        variant="contained"
        size="medium"
        id="goHomeButton"
        className="go-home-button"
        sx={{ marginTop: '1.5rem' }}
      ></Button>
    </Stack>
  );
};
